import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { JWT_VAl } from "../jwt_validate";
import { Helmet } from "react-helmet";
import { createCookie, deleteCookie, getCookie } from "../CookieFunctions";
import apihost from "../ApiHost";

function Login() {
  const re=getCookie("loginRedirect");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      const token = urlParams.get("token");

      if (token) {
        createCookie("_JWT", token);
        if(re){
          window.location.href = re;
          deleteCookie("loginRedirect");
        }else{
          window.location.href = "https://insightnest.tech/";
        }
      }

      const JWT = await JWT_VAl();
      if (JWT.success && JWT.verified) {
        window.location.href = re ? re : "https://insightnest.tech/";
      }
      if (JWT.success && !JWT.verified) {
        navigate("/verify");
      }
    };
    fetchData();
  }, [navigate]);

  const validateInput = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" }); // Clear error for this field
    setServerError(""); // Clear server error
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateInput()) return;

    setIsLoading(true);
    setServerError(""); // Clear any previous server error

    const { email, password } = formData;

    try {
      const response = await fetch(`${apihost}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data._JWT ) {
          createCookie("_JWT", data._JWT);
          window.location.href = re ? re : "https://insightnest.tech/";
        } else if (data.success && data._JWT) {
          createCookie("_JWT", data._JWT);
          navigate("/verify");
        } else {
          setServerError(data.message || "Login failed. Please try again.");
        }
      } else {
        const errorData = await response.json();
        setServerError(errorData.message || "An error occurred. Please try again.");
      }
    } catch (error) {
      setServerError("An error occurred while logging in. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
    <Helmet>
        <title>InsightNest | Login</title>
        <meta name="description" content="Login to InsightNest to get started with Our Tools." />
        <meta name="keywords" content="Login, InsightNest, Ideas, Idea Validation" />
    </Helmet>
    <div className="min-h-screen bg-[rgb(245_243_255)] flex items-center justify-center px-4">
      <div className="max-w-5xl w-full flex flex-col sm:flex-row items-center shadow-lg rounded-lg overflow-hidden">
        {/* Left Section */}
        <div className="sm:w-1/2 w-full bg-[rgb(245_243_255)] flex items-center justify-center p-8">
          <h2 className="text-4xl font-bold text-[rgb(100_65_233)] leading-tight text-center sm:text-left">
            Welcome to <br />
            <span className="text-gray-800">Insight Nest</span>
          </h2>
        </div>

        {/* Right Section - Form */}
        <div className="sm:w-1/2 w-full p-8 bg-white">
          <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center sm:text-left">
            Login
          </h2>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email"
                className={`w-full p-3 mt-1 border rounded-lg ${
                  errors.email ? "border-red-500" : "focus:ring-[rgb(100_65_233)] focus:border-[rgb(100_65_233)]"
                }`}
                required
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Password"
                className={`w-full p-3 mt-1 border rounded-lg ${
                  errors.password ? "border-red-500" : "focus:ring-[rgb(100_65_233)] focus:border-[rgb(100_65_233)]"
                }`}
                required
              />
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password}</p>
              )}
            </div>
            {serverError && (
              <p className="text-red-500 text-sm mb-4">{serverError}</p>
            )}
            <button
              type="submit"
              className={`w-full p-3 rounded-lg font-semibold transition duration-300 ${
                isLoading
                  ? "bg-gray-400 text-gray-700 cursor-not-allowed"
                  : "bg-[rgb(100_65_233)] text-white hover:bg-[rgb(85_50_210)]"
              }`}
              disabled={isLoading}
            >
              {isLoading ? "Logging in..." : "Login"}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center my-6">
            <div className="flex-grow border-t border-gray-300"></div>
            <span className="px-3 text-gray-500 text-sm">OR</span>
            <div className="flex-grow border-t border-gray-300"></div>
          </div>

          {/* Google Login */}
          <a href={`${apihost}/auth/google`}>
            <button
              type="button"
              aria-label="Login with Google"
              className="w-full bg-white border border-gray-300 text-gray-700 p-3 rounded-lg flex items-center justify-center hover:bg-gray-100 transition duration-300"
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/c1/Google_%22G%22_logo.svg/1200px-Google_%22G%22_logo.svg.png"
                alt="Google"
                className="w-5 h-5 mr-2"
              />
              Login with Google
            </button>
          </a>

          {/* Signup Link */}
          <p className="text-sm text-gray-500 mt-6 text-center sm:text-left">
            Don't have an account?{" "}
            <Link to={"/signup"} className="text-[rgb(100_65_233)] hover:underline">
              Sign Up
            </Link>
          </p>
        </div>
      </div>
    </div>
    </>
  );
}

export default Login;
